import {
  DEFAULT_VOICE,
  PushPlatformHint,
  presentVoiceText,
  renderVoiceCopy,
  type PushPlatformHint as PushPlatformHintType,
  type Voice,
} from '@gb-ticket/shared-types';
import { toast } from 'sonner';

export type PushPermissionState = NotificationPermission | 'unsupported';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Where to turn the browser push service back on. The address is browser-specific. */
export type PushSettingsGuide = {
  browserLabel: string;
  settingsUrl: string;
  /** What to switch on after the settings page opens. */
  action: string;
};

type NavigatorLike = {
  userAgent: string;
  brave?: unknown;
};

/**
 * Settings page for the browser that blocked the push service.
 * Brave is the one that hides Google push behind a privacy switch.
 */
export function pushSettingsGuide(nav: NavigatorLike = navigator): PushSettingsGuide | null {
  const ua = nav.userAgent;
  if (nav.brave != null || /Brave/i.test(ua)) {
    return {
      browserLabel: 'Brave',
      settingsUrl: 'brave://settings/privacy',
      action: 'platform.push.brave',
    };
  }
  if (ua.includes('Edg/')) {
    return {
      browserLabel: 'Edge',
      settingsUrl: 'edge://settings/content/notifications',
      action: 'platform.push.allow.site',
    };
  }
  if (/OPR\/|Opera/.test(ua)) {
    return {
      browserLabel: 'Opera',
      settingsUrl: 'opera://settings/content/notifications',
      action: 'platform.push.allow.site',
    };
  }
  if (/Vivaldi/i.test(ua)) {
    return {
      browserLabel: 'Vivaldi',
      settingsUrl: 'vivaldi://settings/privacy',
      action: 'platform.push.allow.site',
    };
  }
  if (ua.includes('Firefox/')) {
    return {
      browserLabel: 'Firefox',
      settingsUrl: 'about:preferences#privacy',
      action: 'platform.push.allow.firefox',
    };
  }
  if (ua.includes('Chrome/')) {
    return {
      browserLabel: 'Chrome',
      settingsUrl: 'chrome://settings/content/notifications',
      action: 'platform.push.allow.site',
    };
  }
  return null;
}

export function isPushServiceUnreachable(error: unknown): boolean {
  return (
    typeof DOMException !== 'undefined' &&
    error instanceof DOMException &&
    (error.name === 'AbortError' || error.name === 'NotSupportedError')
  );
}

/** Opens the browser's own settings page. Internal addresses only work from the address bar in some browsers. */
export function openPushSettings(url: string): void {
  window.location.assign(url);
}

export function pushActivationErrorMessage(error: unknown, voice: Voice = DEFAULT_VOICE): string {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    if (error.name === 'InvalidAccessError' || error.name === 'InvalidCharacterError') {
      return 'Der Push-Schlüssel des Servers ist ungültig. Bitte die IT informieren.';
    }
    if (error.name === 'AbortError' || error.name === 'NotSupportedError') {
      const guide = typeof navigator !== 'undefined' ? pushSettingsGuide() : null;
      if (guide) {
        return renderVoiceCopy('platform.push.service.steps', voice, {
          browser: guide.browserLabel,
          action: presentVoiceText(guide.action, voice),
          url: guide.settingsUrl,
        });
      }
      return renderVoiceCopy('platform.push.service.blocked', voice);
    }
    if (error.name === 'NotAllowedError') {
      return renderVoiceCopy('platform.push.blocked', voice);
    }
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return 'Push konnte nicht aktiviert werden.';
}

/** Toast plus the settings guide, so the next step stays on screen after the toast closes. */
export function reportPushActivationError(
  error: unknown,
  voice: Voice = DEFAULT_VOICE,
): PushSettingsGuide | null {
  if (isPushServiceUnreachable(error)) {
    const guide = typeof navigator !== 'undefined' ? pushSettingsGuide() : null;
    if (guide) {
      toast.error(`${guide.browserLabel} erreicht den Push-Dienst nicht.`, {
        description: `${presentVoiceText(guide.action, voice)} Danach diese Seite neu laden.`,
        duration: 30_000,
        action: {
          label: 'Einstellungen öffnen',
          onClick: () => openPushSettings(guide.settingsUrl),
        },
      });
      return guide;
    }
  }
  toast.error(pushActivationErrorMessage(error, voice));
  return null;
}

export function readNotificationPermission(): PushPermissionState {
  if (!isPushSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function detectPlatformHint(): PushPlatformHintType {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return PushPlatformHint.MOBILE_IOS;
  }
  if (/Android/i.test(ua)) {
    return PushPlatformHint.MOBILE_ANDROID;
  }
  if (/Win/i.test(ua)) {
    return PushPlatformHint.DESKTOP_WINDOWS;
  }
  if (/Mac/i.test(ua)) {
    return PushPlatformHint.DESKTOP_MAC;
  }
  if (/Linux/i.test(ua)) {
    return PushPlatformHint.DESKTOP_LINUX;
  }
  return PushPlatformHint.OTHER;
}

/** Converts a URL-safe base64 VAPID public key to the Uint8Array PushManager expects. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }
  return outputArray;
}

export const PUSH_PLATFORM_HINT_LABELS: Record<PushPlatformHintType, string> = {
  [PushPlatformHint.DESKTOP_WINDOWS]: 'Windows',
  [PushPlatformHint.DESKTOP_MAC]: 'macOS',
  [PushPlatformHint.DESKTOP_LINUX]: 'Linux',
  [PushPlatformHint.MOBILE_IOS]: 'iPhone / iPad',
  [PushPlatformHint.MOBILE_ANDROID]: 'Android',
  [PushPlatformHint.OTHER]: 'Gerät',
};
